/**
 * apps/web :: pass :: screens/ChecklistScreen
 *
 * Reusable trip-phase checklist (ROADS-569 W1.C4). Resolves the traveler's
 * first active `tourist_out` country (fallback "IT"), reads the pack's
 * checklist for the given `phase`, and renders each item as a checkable row
 * with a local-state checkbox, a label + optional detail, and an optional
 * external citation link. A header shows a live "{n}/{total}" progress count.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { useState } from "react";

import PassLayout from "../PassLayout";
import {
  getPack,
  useActivePassCountries,
  type PackId,
} from "@/lib/pass-content";

/** Maps the wrapper-facing phase prop to the pack `checklists` key. */
type Phase = "pre" | "on" | "post";

export interface ChecklistScreenProps {
  /** Header heading (e.g. "Before you leave home"). */
  title: string;
  /** Small uppercase eyebrow above the title. */
  eyebrow: string;
  /** Which trip-lifecycle checklist to render. */
  phase: Phase;
  /** Tailwind text colour class for the accent (eyebrow + progress). */
  accent: string;
}

export default function ChecklistScreen({
  title,
  eyebrow,
  phase,
  accent,
}: ChecklistScreenProps): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const pack = getPack(country as PackId);
  const items = pack.checklists[phase];

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const doneCount = items.reduce(
    (n, item) => (checked[item.id] ? n + 1 : n),
    0,
  );

  function toggle(id: string): void {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <PassLayout country={country} title={`${pack.jurisdiction.name} checklist`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className={`text-xs font-semibold uppercase tracking-wide ${accent}`}
            data-testid="checklist-eyebrow"
          >
            {eyebrow}
          </p>
          <h2 className="mt-0.5 font-display text-lg font-bold text-neutral-900">
            {title}
          </h2>
        </div>
        <span
          data-testid="checklist-progress"
          className={`shrink-0 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold ${accent}`}
        >
          {doneCount}/{items.length}
        </span>
      </div>

      <ul className="mt-lg flex flex-col gap-2">
        {items.map((item) => {
          const isChecked = Boolean(checked[item.id]);
          return (
            <li
              key={item.id}
              data-testid={`checklist-row-${item.id}`}
              data-checked={isChecked}
              className="rounded-md border border-neutral-200 px-md py-sm"
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(item.id)}
                  data-testid={`checklist-check-${item.id}`}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm font-semibold ${
                      isChecked
                        ? "text-neutral-400 line-through"
                        : "text-neutral-900"
                    }`}
                  >
                    {item.label}
                  </span>
                  {item.detail ? (
                    <span className="mt-0.5 block text-sm text-neutral-600">
                      {item.detail}
                    </span>
                  ) : null}
                  {item.citation ? (
                    <a
                      href={item.citation}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`checklist-source-${item.id}`}
                      className="mt-1 inline-block text-xs font-semibold text-indigo-600 underline hover:text-indigo-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Source
                    </a>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </PassLayout>
  );
}
